/**
 * SSE stream failures as a queryable metric, not an app exception.
 *
 * `log.error` reaches console.error in production, and PostHog's
 * `capture_exceptions` promotes console errors to synthetic `$exception`s, so
 * the notification/comment feeds' HANDLED, self-recovering conditions (the
 * per-user connection cap, retry-exhaustion after repeated drops) rendered as
 * crashes in Error Tracking. PostHog issue 019ef960: 10 events / 7 people /
 * 30d, every one a self-healing feed, burying real crashes; plus the
 * 2026-07-31 "SSE connection limit reached" pair. Since 2026-08-04 the server
 * also evicts a capped user's OLDEST stream instead of refusing the newest,
 * so the cap branch means "stream rotated out", never "user locked out".
 *
 * Same class and same shape as api_network_failure (api/networkFailure.ts):
 * the consumers log at warn (verbose-gated, prod consoles stay quiet) and
 * capture ONE `sse_stream_failed` per failure streak, so "why does the feed
 * drop for some sessions" stays answerable in PostHog without minting fake
 * crashes. Consumers: hooks/useNotifications.ts, comments/CommentThread.tsx.
 *
 * Pure module (no logger, no posthog): the SSE consumers themselves read
 * import.meta.env and cannot load under jest; this module carries the
 * behaviorally-testable logic. Same pattern as networkFailure.ts.
 */
import { SSE_DROP_ESCALATION_THRESHOLD } from './sseDropTracker';
import { describeReason } from './api/networkFailure';

export const SSE_STREAM_FAILURE_EVENT = 'sse_stream_failed';

export type SseStreamFailureSurface = 'notifications' | 'comments';

export type SseStreamFailureReason =
  /** Server per-user cap hit: stream rejected/rotated, cooldown scheduled. */
  | 'connection_cap'
  /** Stream-ticket POST answering non-2xx repeatedly. */
  | 'ticket_failing'
  /** Ticket fetch THROWING repeatedly (server unreachable). */
  | 'connect_failing'
  /** EventSource dropping repeatedly without ever subscribing. */
  | 'stream_failing';

export interface SseStreamFailureProps extends Record<string, unknown> {
  surface: SseStreamFailureSurface;
  reason: SseStreamFailureReason;
  consecutiveDrops?: number;
  status?: number;
  detail?: string;
}

/** Build the analytics payload. Absent fields are omitted, never undefined. */
export function buildSseStreamFailureProps(
  surface: SseStreamFailureSurface,
  reason: SseStreamFailureReason,
  extra: { consecutiveDrops?: number; status?: number; error?: unknown } = {},
): SseStreamFailureProps {
  const props: SseStreamFailureProps = { surface, reason };
  if (typeof extra.consecutiveDrops === 'number') props.consecutiveDrops = extra.consecutiveDrops;
  if (typeof extra.status === 'number') props.status = extra.status;
  if ('error' in extra) props.detail = describeReason(extra.error);
  return props;
}

/**
 * Fire the capture exactly once per failure streak: at the drop that CROSSES
 * the escalation threshold. recordDrop() answers 'error' for every drop at
 * and beyond the threshold, and the reconnect ladder retries forever (~2/min
 * at the 30s ceiling), so capturing on every escalated drop would turn one
 * dead server into hundreds of events per abandoned tab.
 */
export function isEscalationCrossing(consecutiveDrops: number): boolean {
  return consecutiveDrops === SSE_DROP_ESCALATION_THRESHOLD;
}
